<template>
  <div
    v-if="geoLocation && userCountry && activeCountry"
    class="update-currency-modal critical-component-clear"
  >
    <h3
      class="update-currency-modal__header"
      v-text="headerText"
    />

    <p
      class="update-currency-modal__desc"
      v-html="descHtml"
    />

    <div
      v-if="activeCountry"
      class="update-currency-modal__option"
    >
      <button
        class="
          update-currency-modal__button
          update-currency-modal__button--continue
          button button--solid
        "
        type="button"
        @click="setCountry(activeCountry, $event)"
      >
        <span
          class="body-2 update-currency-modal__label"
          v-text="$string(
            'currency.continue',
            {
              replace: {
                currency: activeCountry.currency,
                symbol: activeCountry.symbol,
              },
            },
          )"
        />
      </button>
    </div>

    <div
      v-if="userCountry"
      class="update-currency-modal__option"
    >
      <button
        class="
          update-currency-modal__button
          update-currency-modal__button--switch
          button button--text
        "
        type="button"
        @click="setCountry(userCountry, $event)"
      >
        <span
          class="body-2 update-currency-modal__label"
          v-text="$string(
            'currency.switch',
            {
              replace: {
                currency: userCountry.currency,
                symbol: userCountry.symbol,
              },
            },
          )"
        />

        <span class="icon"><icon-arrow-horizontal /></span>
      </button>
    </div>

    <input
      v-model="selectedCountry"
      type="hidden"
      name="country_code"
    >
  </div>
</template>

<script>

/**
 * Vue: Update currency modal (update-currency-modal)
 * -----------------------------------------------------------------------------
 * Modal prompt to allow users to select currency.
 *
 * @param {Number} liquid.cookieDismiss - Number of days to save cookie.
 * @param {Object} localization - Shopify localization object.
 *
 */
import { nextTick } from 'vue'
import Cookies from 'js-cookie'
import { mapActions } from 'vuex'
import IconArrowHorizontal from '~icons/direction/arrow/horizontal.svg'

export default {
  name: 'UpdateCurrencyModal',

  components: {
    IconArrowHorizontal,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        cookieDismiss: 1,
      }),
    },
    localization: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },

  data() {
    return {
      geoLocation: '',
      selectedCountry: this.localization.current,
    }
  },

  computed: {

    /**
     * Compute header text.
     * @returns {String}
     */
    headerText() {
      return this.$string(
        'currency.title',
        {
          replace: {
            country: this.geoLocation,
          },
        },
      )
    },

    /**
     * Compute description text.
     * @returns {String}
     */
    descHtml() {
      let bp1 = this.$string('currency.description_bullet_point_1')
      let bp2 = this.$string('currency.description_bullet_point_2')
      let bp3 = this.$string('currency.description_bullet_point_3')

      if (this.userCountry.code === 'US') {
        const thresholdText = `${this.userCountry.symbol}${this.liquid.freeShippingThreshold.toFixed(2)}`
        bp1 = bp1.replace('{{ amount }}', thresholdText)
        bp2 = bp2.replace('{{ amount }}', thresholdText)
        bp3 = bp3.replace('{{ amount }}', thresholdText)
      } else {
        bp1 = bp1.includes('{{ amount }}') ? '' : bp1
        bp2 = bp2.includes('{{ amount }}') ? '' : bp2
        bp3 = bp3.includes('{{ amount }}') ? '' : bp3
      }

      return bp1?.length || bp2?.length || bp3?.length
        ? `
        <ul>
          ${bp1?.length ? `<li>${bp1}</li>` : ''}
          ${bp2?.length ? `<li>${bp2}</li>` : ''}
          ${bp3?.length ? `<li>${bp3}</li>` : ''}
        </ul>
      `
        : ''
    },

    /**
     * Compute user's country based on location.
     * @returns {Object}
     */
    userCountry() {
      const current = this.localization.countries.find(country => {
        return country.name === this.geoLocation
      })

      if (!current) {
        return false
      }

      return current
    },

    /**
   * Compute active country.
   * @returns {Object}
   */
    activeCountry() {
      return this.localization.current
    },
  },

  mounted() {
    this.openModal()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Query geolocation from Shopify geolocation app.
     * @returns {String}
     */
    async queryGeolocation() {
      try {
        let response = await fetch('/browsing_context_suggestions.json')

        response = await response.json()

        const countryName = response.detected_values.country_name

        return countryName

      } catch (error) {
        throw new Error('Failed to fetch geolocation', error)
      }
    },

    /**
     * Set country based on user selection, submit form.
     * @param {Object} country - Country object.
     * @param {Event} event - Click event.
     */
    setCountry(country, event) {
      const form = event.target.closest('form')

      this.selectedCountry = country.code

      this.dismissModal()

      nextTick().then(() => {
        form.submit()
      })
    },

    /**
     * Open update-currency modal.
     */
    openModal() {
      if (Cookies.get('updateCurrencyDismissed') || !this.activeCountry) {
        return
      }

      /**
       * Get geolocation and determine whether to open the modal.
       */
      this.queryGeolocation().then((response) => {
        this.geoLocation = response

        if (!this.geoLocation || this.geoLocation === this.activeCountry.name) {
          return
        }

        // Only open prompt if user is located in one of the markets
        this.$nextTick().then(() => {
          if (!this.userCountry) {
            return
          }

          this.openOverlay({
            ignoreDismissed: true,
            namespace: 'updateCurrencyModal',
          })
        })
      })
    },

    /**
     * Set currency dismissed cookie
     */
    dismissModal() {
      Cookies.set(
        'updateCurrencyDismissed',
        true,
        { expires: this.liquid.cookieDismiss },
      )
    },

    /**
     * Convert from store base currency to requested one
     * @param {Number} amount - Amount to convert
     * @param {String} currencyCode - Currency iso_code to convert to
     * @returns {Number} Converted amount
     */
    convertAmountToCurrency(amount, currencyCode) {
      return window.Currency?.convert(
        100,
        'USD',
        currencyCode,
      ) || amount
    },
  },
}
</script>

<style lang="scss">
@import './update-currency-modal';
</style>
